import { createRoot } from 'react-dom/client';

// Renders a React element (the same shared Letterhead components the Billing
// Settings live preview uses) into a new browser tab and hands it straight to
// the browser's own print pipeline — the same "Save as PDF" flow every
// browser already has, instead of rasterizing the DOM into a giant PNG and
// wrapping that image in jsPDF.
//
// That rasterize-then-wrap approach is what caused an earlier 274-page, 26MB
// PDF regression (see Finance.js history), and it's fundamentally the wrong
// tool here: a tall document (many rows, long text) produces a very tall
// off-screen canvas, and different browsers' canvas/PDF-page-size limits
// disagree on how to handle that — sometimes silently splitting or
// re-tiling it into dozens of oversized pages, and always turning crisp
// text into a blurry embedded image (which is also why the file balloons to
// tens of MB — a screenshot of text is a much bigger PDF payload than the
// text itself). Printing the real DOM lets the browser's print engine lay it
// out onto proper A4 pages with real, selectable, small-file-size text —
// exactly what the "Save as PDF" print-dialog option already does well.
// The print dialog itself is what gives the user the "save wherever I want"
// choice; there's no separate download step to automate around that.
// Fonts + base styles of the blank document letterheads are rendered into.
// Shared with the Android apps' PDF renderer (mobile/pdf.js) so a document
// looks the same printed from the web or saved on a phone.
export const PRINT_DOC_HEAD = `
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        @page { size: A4; margin: 10mm; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        html, body { margin: 0; padding: 0; }
      </style>`;

// `element` may also be a Promise (or a function returning one) of
// { element, title }: the tab is opened straight away on the click, then filled
// once the data arrives — browsers block tabs opened after a network wait.
export function printComponent(element, docTitle) {
  return new Promise((resolve, reject) => {
    const printWin = window.open('', '_blank');
    if (!printWin) { reject(new Error('Pop-up blocked — please allow pop-ups for this site and try again.')); return; }
    const lazy = typeof element === 'function' || (element && typeof element.then === 'function');
    printWin.document.write(`<!DOCTYPE html><html><head><title>${lazy ? 'Loading…' : docTitle}</title>${PRINT_DOC_HEAD}
    </head><body><div id="print-root">${lazy ? '<p style="font:14px sans-serif;padding:24px;color:#6b7280">Preparing your document…</p>' : ''}</div></body></html>`);
    printWin.document.close();

    const mountAndPrint = async () => {
      let el = element;
      if (lazy) {
        try {
          const doc = await (typeof element === 'function' ? element() : element);
          el = doc.element;
          printWin.document.title = doc.title || '';
        } catch (e) { printWin.close(); reject(e); return; }
      }
      const container = printWin.document.getElementById('print-root');
      container.innerHTML = '';
      const root = createRoot(container);
      root.render(el);
      // Give fonts/images a moment to load before invoking print — the
      // window's own onload already waited for the shell document, not for
      // the React tree we just mounted into it.
      setTimeout(() => {
        printWin.focus();
        printWin.print();
        resolve();
      }, 450);
    };

    if (printWin.document.readyState === 'complete') mountAndPrint();
    else printWin.onload = mountAndPrint;
  });
}
